import { ReactElement, useState } from 'react';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { TableSection } from 'common/components/TableSection/TableSection';
import TokenModal from 'common/components/token-modal/token-modal.component';
import { ETokenModalType } from 'common/components/token-modal/token-modal.enum';
import { ENV_ACCOUNT_GUID, ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE, IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { useCounterpartyAccountDetailsQuery } from 'domains/accounts/queries/accounts.query';
import { TCounterpartiesListResData } from 'domains/counterparties/types/counterparty.types';
import EmissionHistoryTable from 'domains/emission/components/emission-history-table/emission-history-table.component';
import Card from 'pages/dashboard/components/card/card.component';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ReactComponent as TotalCostSvg } from 'assets/ibm/cost-total.svg';
import { ReactComponent as WalletSvg } from 'assets/ibm/wallet.svg';
import { SContainer, SHeader } from './dashboard.page.style';
import CounterpartiesTable from './tables/counterparties-table/counterparties-table.component';
import { TDashboardPageLoaderData } from './types/dashboard.types';

const DashboardPage = (): ReactElement => {
  const { t } = useTranslation();
  const [issueTokenModalOpen, setIssueTokenModalOpen] = useState(false);
  const [burnTokenModalOpen, setBurnTokenModalOpen] = useState(false);
  const { counterpartiesData: loaderCounterpartiesData, accountListData: loaderAccountListData } =
    useLoaderData() as TDashboardPageLoaderData;

  const { data: accountListData } = useCounterpartyAccountDetailsQuery(
    ENV_COUNTERPARTY_GUID,
    ENV_ACCOUNT_GUID,
    {},
    {
      initialData: loaderAccountListData,
      refetchOnMount: false,
      enabled: IS_ADMIN_FE,
    },
  );

  const rootWalletBalance = accountListData?.assets?.[0]?.balance;

  return (
    <SContainer>
      <Space
        direction="vertical"
        size={24}
        style={{
          width: '100%',
        }}
      >
        <SHeader>
          <Space size={16}>
            <Card value="73 291 123" title={t('dashboard.assetsInUse')} icon={<TotalCostSvg />} />
            {IS_ADMIN_FE &&
              <Card
                title={t('dashboard.balance')}
                value={rootWalletBalance || 0}
                icon={<WalletSvg />}
              />}
          </Space>
          {IS_BANK_FE && (
            <Space>
              <Button
                warning
                width={147}
                height={32}
                disabled={rootWalletBalance === 0}
                onClick={(): void => {
                  setBurnTokenModalOpen(true);
                }}
              >
                {t('common.burn')}
              </Button>
              <TokenModal
                type={ETokenModalType.Burn}
                maxAmount={rootWalletBalance}
                open={burnTokenModalOpen}
                onCancel={(): void => {
                  setBurnTokenModalOpen(false);
                }}
              />
              <Button
                success
                width={147}
                height={32}
                onClick={(): void => {
                  setIssueTokenModalOpen(true);
                }}
              >
                {t('common.issue')}
              </Button>
              <TokenModal
                type={ETokenModalType.Issue}
                open={issueTokenModalOpen}
                onCancel={(): void =>  {
                  setIssueTokenModalOpen(false);
                }}
              />
            </Space>
          )}
        </SHeader>
        <Space
          direction="vertical"
          size={32}
          style={{
            width: '100%',
          }}
        >
          <TableSection
            href={ROUTES_MAPPING.PROTECTED.REPORTING_EMISSION_HISTORY}
            title={t('dashboard.emissionHistoryTable.title')}
          >
            <EmissionHistoryTable isDashboard counterpartiesInitialData={loaderCounterpartiesData} />
          </TableSection>
          {!IS_BANK_FE && (
            <TableSection title={t('dashboard.counterpartiesTable.title')}>
              <CounterpartiesTable data={loaderCounterpartiesData} />
            </TableSection>
          )}
        </Space>
      </Space>
    </SContainer>
  );
};

export const loader = async (): Promise<{
  counterpartiesData: TCounterpartiesListResData | {},
  accountListData: TCounterpartiesListResData | {},
}> => {
  try {
    let accountListData: TCounterpartiesListResData | {} = {};

    if (IS_ADMIN_FE) {
      const accountListQuery = queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccountDetails(
        ENV_COUNTERPARTY_GUID,
        ENV_ACCOUNT_GUID,
      );
      accountListData =
        queryClient.getQueryData(accountListQuery.queryKey) ??
        (await queryClient.fetchQuery(accountListQuery));
    }

    const counterpartiesListQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();

    const counterpartiesData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesListQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesListQuery));

    return {
      counterpartiesData,
      accountListData,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesData: {},
      accountListData: {},
    };
  }
};

export default DashboardPage;
